import { normalizeFieldsConfig } from '@alliance-strategies/form-engine-portable';
import type {
  PassportFieldChangeScopePort, PassportFormPort, PassportGuardPort, PassportRequestPort,
} from './passportRepairedContract';
import { hashTrackedValues, type PassportProvenanceMetadata, type ProvenanceSnapshot } from './passportProvenance';

const NATIONALITY_FIELDS = ['passport_nationality', 'passportNationality'] as const;
const DOB_FIELDS = ['passport_date_of_birth', 'passportDateOfBirth', 'passport_dob', 'passportDob'] as const;

export type CanonicalPassportTargets = Readonly<{
  nationality?: typeof NATIONALITY_FIELDS[number];
  dateOfBirth?: typeof DOB_FIELDS[number];
}>;

/**
 * Only explicitly configured public field names are eligible. Stored RHF
 * values can outlive a schema and never establish registration. Missing or
 * multiple distinct members of a family are deliberately ineligible: do not
 * create hidden DOB fields in legacy versions or choose between aliases.
 */
export function canonicalPassportTargets(fieldsConfig: unknown): CanonicalPassportTargets {
  const names = new Set<string>();
  const object = (value: unknown): Record<string, unknown> =>
    value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
  // Use the renderer's public normalization for catalogue keys, empty section
  // fields, and key-only definitions; do not reproduce its resolution rules.
  const config = normalizeFieldsConfig(object(fieldsConfig));
  const layout = object(config.layout);
  const fieldName = (field: unknown) => {
    const row = object(field);
    const name = row.name || row.id;
    return typeof name === 'string' ? name : undefined;
  };
  const addFields = (fields: unknown) => {
    if (!Array.isArray(fields)) return;
    for (const field of fields) {
      const name = fieldName(field);
      if (name) names.add(name);
    }
  };
  const addContainers = (containers: unknown) => {
    if (!Array.isArray(containers)) return;
    for (const container of containers) {
      const row = object(container);
      addFields(row.fields);
    }
  };
  // Match the public layout's selected field containers. A root fields list
  // may be a definition catalogue for sections, not rendered flat fields.
  if (layout.type === 'tabbed') addContainers(layout.tabs);
  else if (layout.type === 'multi_step') addContainers(layout.sections);
  else {
    const sections = layout.sections ?? config.sections;
    if (Array.isArray(sections) && sections.length) addContainers(sections);
    else {
      addContainers(layout.tabs);
      addContainers(layout.sections);
      addFields(layout.fields);
      addFields(config.fields);
    }
  }
  const nationality = NATIONALITY_FIELDS.filter(name => names.has(name));
  const dateOfBirth = DOB_FIELDS.filter(name => names.has(name));
  return {
    nationality: nationality.length === 1 ? nationality.at(0) : undefined,
    dateOfBirth: dateOfBirth.length === 1 ? dateOfBirth.at(0) : undefined,
  };
}

/** An edit to a served alias also protects that identity field from OCR. */
export function canonicalPassportFieldFamily(fieldName: string): readonly string[] {
  if (NATIONALITY_FIELDS.some(field => field === fieldName)) return NATIONALITY_FIELDS;
  if (DOB_FIELDS.some(field => field === fieldName)) return DOB_FIELDS;
  return [fieldName];
}

/** Project only final wire copies and their accepted receipt; never edit a draft. */
export function prepareCanonicalPassportSubmission(
  values: Record<string, unknown>,
  metadata: PassportProvenanceMetadata | null,
  snapshot: ProvenanceSnapshot,
  targets: CanonicalPassportTargets,
) {
  const excluded = new Set<string>();
  for (const [family, target] of [
    [NATIONALITY_FIELDS, targets.nationality], [DOB_FIELDS, targets.dateOfBirth],
  ] as const) {
    if (!target) {
      if (family.some(field => values[field] != null && values[field] !== '')) {
        throw new Error('Passport nationality or date-of-birth field configuration is unavailable or ambiguous. Please reload before saving.');
      }
      continue;
    }
    for (const field of family) if (field !== target) excluded.add(field);
  }
  const responses = Object.fromEntries(Object.entries(values).filter(([field]) => !excluded.has(field)));
  return {
    responses,
    metadata: metadata ? { ...metadata, fields: metadata.fields.filter(field => !excluded.has(field.field)) } : null,
    receipt: {
      ...snapshot,
      valuesHash: hashTrackedValues(responses),
      fields: Object.fromEntries(Object.entries(snapshot.fields)
        .filter(([field]) => !excluded.has(field))
        .map(([field, evidence]) => [field, { ...evidence, userEdited: false }])),
    },
  };
}

/** Date-only calendar validation, without parsing timezones or guessing formats. */
function isDateOnly(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  if (year < 1 || month < 1 || month > 12 || day < 1) return false;
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = month === 2 ? (leap ? 29 : 28) : ([4, 6, 9, 11].includes(month) ? 30 : 31);
  return day <= days;
}

/**
 * Hub canonical targets absent from the 2.46 helper's fixed map. These are form
 * captures only, not canonical identity writes or authenticated OCR evidence.
 * StepBehaviorRegistry explicitly permits custom writes under public runTagged.
 * The caller has already checked the shared whole-response permission.
 */
export function applyCanonicalPassportFields(
  form: PassportFormPort,
  data: Record<string, unknown>,
  scope: PassportFieldChangeScopePort,
  guard: PassportGuardPort,
  request: PassportRequestPort,
  isCurrent: () => boolean,
  targets: CanonicalPassportTargets,
): string[] {
  const nationality = data.nationalityCode ?? data.nationality;
  const fields: Array<[string | undefined, unknown]> = [
    [targets.nationality, nationality],
    [targets.dateOfBirth, isDateOnly(data.dateOfBirth) ? data.dateOfBirth : undefined],
  ];
  const applied: string[] = [];
  const tag = {
    origin: 'extraction' as const,
    extraction: {
      kind: 'passport' as const, uploadFieldKey: request.uploadFieldKey,
      uploadFieldName: request.uploadFieldName, requestId: request.requestId,
    },
  };
  for (const [fieldName, value] of fields) {
    if (!fieldName || typeof value !== 'string' || !value.trim() || !isCurrent()) continue;
    const currentValue = form.getValues?.(fieldName);
    if (guard.shouldApplyField?.({ ...request, fieldName, value, currentValue }) === false) continue;
    // A guard can synchronously invalidate ownership or edit the form.
    if (!isCurrent()) continue;
    scope.runTagged(tag, () => {
      if (!isCurrent()) return;
      form.setValue(fieldName, value, { shouldValidate: true, shouldDirty: true });
      applied.push(fieldName);
    });
  }
  return applied;
}